// Copyright (c) 2025 Audere, All Rights Reserved

import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { createStyleSheet, globalStyles } from "../utils/styles";
import { theme } from "../../theme";
import assignmentTurnedInUrl from "../../assets/assignment_turned_in.svg";
import type { AudereSupportedTestsProps, TestItem } from "../types/projects";
import TestItemComponent from "./TestItem";
import { useCallback } from "react";

const AudereSupportedTests = (props: AudereSupportedTestsProps) => {
  const { audereSupportedTests, selectedTestIds, onSelectionChange } = props;
  const { t } = useTranslation("AudereSupportedTests");
  const isMobileView = useMediaQuery("(max-width:500px)");

  const getGroupTestIds = useCallback(
    (group: { rdts: TestItem[] }) => group.rdts?.map(item => item.rdt_uid),
    []
  );
  const handleSelectAll = useCallback(
    (group: { rdts: TestItem[] }, checked: boolean) => {
      const groupIds = getGroupTestIds(group);
      let newSelected: string[];
      if (checked) {
        // Add all group IDs (avoid duplicates)
        newSelected = Array.from(new Set([...selectedTestIds, ...groupIds]));
      } else {
        // Remove all group IDs
        newSelected = selectedTestIds.filter(id => !groupIds.includes(id));
      }
      onSelectionChange(newSelected);
    },
    [getGroupTestIds, onSelectionChange, selectedTestIds]
  );
  const handleTestToggle = useCallback(
    (testId: string, checked: boolean) => {
      let newSelected: string[];
      if (checked) {
        newSelected = Array.from(new Set([...selectedTestIds, testId]));
      } else {
        newSelected = selectedTestIds.filter(id => id !== testId);
      }
      onSelectionChange(newSelected);
    },
    [onSelectionChange, selectedTestIds]
  );

  return (
    <Box sx={styles.audereTestsContainer}>
      <Stack sx={styles.audereTestsHeader}>
        <Box
          component="img"
          src={assignmentTurnedInUrl}
          sx={styles.audereTestImg}
          alt="Assignment turned in"
        />
        <Stack>
          <Typography variant="body1bold">{t("healthPulseAITestsText")}</Typography>
          <Typography variant="body2">{t("audereTestInfo")}</Typography>
        </Stack>
      </Stack>
      <Divider />
      <Box px={2} pb={2}>
        {audereSupportedTests?.length ? (
          audereSupportedTests.map((group, index) => {
            const groupIds = getGroupTestIds(group);
            const allSelected = groupIds?.every(id => selectedTestIds.includes(id));
            const someSelected = groupIds?.some(id => selectedTestIds.includes(id));
            return (
              <Grid
                container
                direction="row"
                key={index}
                rowSpacing={1}
                columnSpacing={{ xs: 1, sm: 2, md: 3 }}
                sx={globalStyles.gapTop}
              >
                <Grid size={12}>
                  <Stack sx={styles.audereTestGroupHeader}>
                    <Typography variant="h6">{group.condition}</Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={allSelected}
                          indeterminate={!allSelected && someSelected}
                          onChange={e => handleSelectAll(group, e.target.checked)}
                          sx={{ mr: 1 }}
                        />
                      }
                      label={t("selectAll")}
                    />
                  </Stack>
                </Grid>
                {group.rdts?.map((testItem, index) => (
                  <Grid
                    key={index}
                    size={{ xs: isMobileView ? 12 : 6, sm: 4 }}
                    sx={styles.audereTestItemContainer}
                  >
                    <TestItemComponent
                      testItem={testItem}
                      isTestSelected={selectedTestIds.includes(testItem.rdt_uid)}
                      toggleTestSelection={(checked: boolean) =>
                        handleTestToggle(testItem.rdt_uid, checked)
                      }
                    />
                  </Grid>
                ))}
              </Grid>
            );
          })
        ) : (
          <Box sx={styles.noTestAvailable}>
            <Typography variant="body2bold">{t("noTestAvailable")}</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AudereSupportedTests;

const styles = createStyleSheet({
  audereTestsContainer: {
    background: theme.palette.common.white,
    borderRadius: "6px",
  },
  audereTestsHeader: {
    p: 1,
    px: 2,
    flexDirection: "row",
    justifyContent: "start",
    alignItems: "center",
    gap: 2,
  },
  audereTestImg: { height: theme.spacing(3), objectFit: "contain" },
  audereTestGroupHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  noTestAvailable: {
    p: 2,
    textAlign: "center",
  },
  audereTestItemContainer: {
    ...globalStyles.gapTop,
    display: "flex",
    justifyContent: "center",
    minWidth: "175px",
  },
});
